import axios from "./axios";

/**
 * Client for the Wildlife Insights migration endpoints (AKL-747).
 *
 * "Migration", not "import": in akili, import already means registering
 * already-uploaded photos into the database (AKL-451 / AKL-597). This feature brings a
 * researcher's existing Wildlife Insights *metadata* across — their project settings,
 * deployments, and historical identifications. Photo bytes keep arriving through Akili
 * Transfer and the existing photo import, unchanged.
 */

export type MigrationStatus =
  | "uploaded"
  | "planning"
  | "planned"
  | "applying"
  | "applied"
  | "failed";

/** What the user asked for. Sent as a JSON string in the multipart form. */
export type MigrationOptionsType = {
  org_id: number;
  /**
   * Migrate into this existing project. This is the path for the second pass, and for
   * attaching Wildlife Insights identifications to photos already in akili.
   */
  target_project_id?: number | null;
  /** Create the project described by projects.csv when nothing in akili matches it. */
  create_project?: boolean;
  /**
   * IANA time zone for a newly created project. Required when `create_project` is set:
   * Wildlife Insights does not publish one, and akili requires it (AKL-722) precisely so
   * that nobody infers it.
   */
  timezone?: string | null;
  create_missing_deployments?: boolean;
  /**
   * Flag migrated photos as reviewed. A photo identified in Wildlife Insights has been
   * reviewed by a person, so leaving it unreviewed would put years of finished work back
   * in someone's review queue.
   */
  mark_reviewed?: boolean;
  /**
   * AKL-754 round 2: bring Wildlife Insights' unreviewed AI guesses across as suggestions
   * for the images no person identified there. The server defaults it to true, so an
   * omitted value behaves as round 1 shipped; false leaves those images with no suggestion
   * and counts the guesses under the report's `computer_vision` skip reason.
   */
  migrate_ai_predictions?: boolean;
  /**
   * AKL-813: the format of a CSV import (`akili_csv` or `ctms_csv`). Omitted for a Wildlife
   * Insights bundle, where the server defaults it to `wildlife_insights`. The server chooses
   * how to read the upload — and the shape of the resulting plan — from this field.
   */
  source_format?: MigrationSourceFormat;
  /**
   * AKL-813: the entities a CSV import contains (`["sensors"]` for AKL-813). Omitted for a
   * Wildlife Insights bundle, which always migrates everything it holds. Together with a CSV
   * `source_format` this is what makes the request an organisation-level import rather than a
   * project migration.
   */
  entities?: MigrationEntity[];
};

/**
 * One entry in the report's `warnings` or `blocking` list.
 *
 * A code plus parameters rather than a sentence, because the report is stored once on the
 * job row and read later — possibly by someone using a different language. The wording
 * lives in the translation catalogs; see `migrationNoticeText`.
 */
export type MigrationNotice = {
  code: string;
  /**
   * Optional because a notice with no parameters is emitted without the key at all, and
   * because a code this front end does not know may arrive with any shape. Typing it as
   * required forced casts at the only call site that matters (AKL-747 review).
   */
  params?: Record<string, unknown>;
};

export type MigrationEntityCounts = {
  create: number;
  matched: number;
  /** Locations only: WI gave no coordinates, and location lat/long are NOT NULL. */
  skipped_no_coordinates?: number;
  /** Deployments only. */
  skipped?: number;
};

export type MigrationIdentificationCounts = {
  rows: number;
  create: number;
  already_imported: number;
  media_marked_reviewed: number;
  is_blank_disagreed_with_taxon: number;
  /**
   * Wildlife Insights AI guesses stored as predictions for images no person identified
   * (AKL-754), and the ones an earlier run had already stored. Absent from reports stored
   * before AKL-754; treat missing as 0.
   */
  predictions_created?: number;
  predictions_already_imported?: number;
  skipped: {
    /**
     * Historical: AI guesses dropped by a migration run before AKL-754. A new run reports 0
     * here and counts the guesses under the two AKL-754 reasons below instead.
     */
    computer_vision: number;
    /** A person had identified the image, so the guess was not stored (AKL-754). */
    computer_vision_superseded?: number;
    /** The seeded Wildlife Insights model row is missing on this environment (AKL-754). */
    prediction_model_unavailable?: number;
    qa_artifact: number;
    taxon_not_found: number;
    no_taxon_id: number;
    no_filename: number;
    media_not_found: number;
    media_ambiguous: number;
    /**
     * AKL-1125: an uploaded file has the same name but its time disagrees with the one in
     * the export. Optional because reports stored before AKL-1125 do not carry it.
     */
    media_times_differ?: number;
    /**
     * AKL-1125: the file name matched but the uploaded file's time has not been read yet,
     * and the time is what tells same-named images apart. Optional like the one above.
     */
    media_awaiting_time?: number;
    deployment_not_found: number;
    deployment_not_created_yet: number;
    sequence_fan_out_unavailable: number;
  };
};

/** How a Wildlife Insights image was matched to an uploaded file (AKL-1125). */
export type MigrationMatchStep = "wi_file_id" | "name_and_time" | "name_only";

/** Why a Wildlife Insights image was not matched to an uploaded file (AKL-1125). */
export type MigrationMatchReason =
  | "media_not_found"
  | "media_ambiguous"
  | "media_times_differ"
  | "media_awaiting_time";

/** One deployment's share of the matching summary. Counts are images, not rows. */
export type MigrationDeploymentMatching = {
  wi_deployment_id: string;
  photos: number;
  matched: number;
  by_step: Partial<Record<MigrationMatchStep, number>>;
  unmatched: Partial<Record<MigrationMatchReason, number>>;
  /**
   * The difference between the times in the export and the times in the uploaded files,
   * in seconds (export minus file), when the deployment's times look edited; null otherwise.
   */
  shift_seconds: number | null;
  /** Images whose match needed that difference. */
  shifted_photos: number;
  akili_photos: number;
  /** Uploaded images of this deployment whose time has not been read yet. */
  akili_photos_awaiting_time: number;
};

/**
 * How a project's Wildlife Insights images were matched to uploaded files (AKL-1125).
 *
 * Counts are images, not identification rows: an image with two species is one image here
 * and two rows in `identifications`. Optional on the section and totals because reports
 * stored before AKL-1125 do not carry it.
 */
export type MigrationMatching = {
  photos: number;
  matched: number;
  by_step: Partial<Record<MigrationMatchStep, number>>;
  unmatched: Partial<Record<MigrationMatchReason, number>>;
  /** Images whose match needed a deployment's clock difference. */
  shifted_photos: number;
  deployments_shifted: number;
  /** Uploaded images whose time has not been read yet, across the matched deployments. */
  akili_photos_awaiting_time: number;
  /** Worst-first and capped by the server; `deployments_omitted` counts the rest. */
  deployments: MigrationDeploymentMatching[];
  deployments_omitted: number;
};

/**
 * What the migration does to a project's deployment template (AKL-817).
 *
 * `apply` reads as "will be applied" on a preview and "was applied" on a result — the
 * renderer knows which it is showing. `blocked` accompanies the bundle-level
 * `project_deployment_template_differs` notice; `unavailable` the
 * `deployment_template_unavailable` warning.
 */
export type MigrationTemplateOutcome =
  | "apply"
  | "already_applied"
  | "blocked"
  | "unavailable";

/** What happened to one Wildlife Insights project in the bundle. */
export type MigrationProjectSection = {
  /** `skipped` means this project was not migrated — see its own `blocking`. */
  action: "create" | "match" | "target" | "skipped" | null;
  project_id: number | null;
  project_name?: string | null;
  wi_project_id: string | null;
  wi_project_type: string | null;
  fields: Record<string, unknown>;
  /** Stable akili field keys, not display labels — the UI owns the wording. */
  missing_required: string[];
  sensors: MigrationEntityCounts;
  locations: MigrationEntityCounts;
  deployments: MigrationEntityCounts;
  identifications: MigrationIdentificationCounts;
  /**
   * Reasons THIS project was skipped, as distinct from the report's bundle-level list.
   * A blocked project does not stop the others: on an initiative download you may own
   * eight of ten projects.
   */
  blocking: MigrationNotice[];
  /**
   * The deployment template outcome for this project (AKL-817). `outcome` is null when the
   * project was not resolved far enough to decide; `name` is the template's display name,
   * absent when the template row could not be found. Optional because reports stored
   * before AKL-817 have no key at all.
   */
  deployment_template?: {
    outcome: MigrationTemplateOutcome | null;
    name?: string | null;
  } | null;
  /** How this project's images were matched to uploaded files (AKL-1125). */
  matching?: MigrationMatching | null;
};

export type MigrationTotals = {
  projects: number;
  projects_migrated: number;
  /**
   * Projects the migration will create — only ever non-zero on a plan, since on an
   * apply the created project has a row and counts as migrated. Optional because a
   * report produced before AKL-816 does not carry it; treat missing as 0.
   */
  projects_to_create?: number;
  /** Blocked projects only (see each section's `blocking`), not ones still to be created. */
  projects_skipped: number;
  sensors: MigrationEntityCounts;
  locations: MigrationEntityCounts;
  deployments: MigrationEntityCounts;
  identifications: MigrationIdentificationCounts;
  matching?: MigrationMatching | null;
};

export type MigrationPlan = {
  dry_run: boolean;
  bundle: {
    identification_level: "image" | "sequence";
    files: { name: string; columns: number; size_bytes: number | null }[];
    unrecognised_columns: Record<string, string[]>;
    missing_columns: Record<string, string[]>;
  };
  /**
   * One entry per Wildlife Insights project in the bundle. A WI download is not
   * necessarily one project — `project_id` is on every table in the export, and
   * public/initiative downloads span many.
   */
  projects: MigrationProjectSection[];
  totals: MigrationTotals;
  /** Capped examples of each problem, so the user can act rather than only count. */
  samples: Record<string, string[]>;
  warnings: MigrationNotice[];
  /**
   * Bundle-level blockers only. Non-empty means apply is refused outright; a single
   * project's problems live on its own section and merely skip that project.
   */
  blocking: MigrationNotice[];
};

export type MigrationJobDto = {
  guid: string;
  org_id: number;
  project_id: number | null;
  status: MigrationStatus;
  source_format: string;
  bundle_file_name: string | null;
  bundle_size_bytes: number | null;
  options: MigrationOptionsType;
  /** The dry run. Present once status reaches "planned". */
  plan: MigrationPlan | null;
  /** What apply actually did. Present once status reaches "applied". */
  result: MigrationPlan | null;
  error: string | null;
  created_at: string | null;
  planned_at: string | null;
  applied_at: string | null;
};

export type MigrationListDto = {
  migrations: MigrationJobDto[];
};

/**
 * How the server reads an upload (AKL-813). `wildlife_insights` is a WI export bundle; the
 * two CSV formats are a single loose file — akili's own column layout, or Wildlife Insights'
 * CTMS bulk-upload template (`cameras.csv` for sensors).
 */
export type MigrationSourceFormat =
  | "wildlife_insights"
  | "akili_csv"
  | "ctms_csv";

/** What a CSV import can contain. AKL-813 supports sensors; AKL-814/815 add the others. */
export type MigrationEntity = "sensors" | "locations" | "deployments";

/** The formats a CSV import can name — every source format except the WI bundle. */
export type CsvSourceFormat = Exclude<
  MigrationSourceFormat,
  "wildlife_insights"
>;

/**
 * The options a CSV import sends (AKL-813). Organisation-level for sensors: no project is
 * targeted or created. `target_project_id` is reserved for the project-scoped imports of
 * AKL-814/815 and is omitted (not sent as null) when there is none.
 */
export type CsvImportOptionsType = {
  org_id: number;
  source_format: CsvSourceFormat;
  entities: MigrationEntity[];
  target_project_id?: number | null;
};

/**
 * One row the import did not create, with the reason as a code plus parameters — the UI owns
 * the wording (see `csvSkipReasonText`). `row` is the 1-based physical record number, so it
 * matches the row a spreadsheet shows.
 */
export type CsvImportSkippedRow = {
  row: number;
  code: string;
  params?: Record<string, unknown>;
};

/** Per-entity counts of a CSV import. `create + matched + skipped === rows` always holds. */
export type CsvImportEntityCounts = {
  rows: number;
  create: number;
  matched: number;
  skipped: number;
  skipped_by_code: Record<string, number>;
};

/**
 * The target project of a project-scoped CSV import (AKL-814). Written by the server so the
 * job appears in the project's migration history (`GET /migrations/project/{id}`) alongside
 * the Wildlife Insights migrations; `action` is always `target` because a CSV import never
 * creates or matches a project. The web does not read it yet — no surface renders that
 * history — but typing it here keeps the plan shape honest.
 */
export type CsvImportProjectSection = {
  action: "target";
  project_id: number;
  project_name: string | null;
  /**
   * The slot a Wildlife Insights migration fills with the WI project id. A CSV import has
   * no WI id, so the server writes the akili `project_id` as text, e.g. `"7"`.
   */
  wi_project_id: string;
};

/**
 * The plan (dry run) or result of a CSV import (AKL-813).
 *
 * The same job endpoints return this or a `MigrationPlan` depending on the job's
 * `source_format`; `kind === "csv_import"` is the discriminator (a WI plan carries no `kind`).
 * Only the entity sections the import contained are present.
 */
export type CsvImportPlan = {
  kind: "csv_import";
  dry_run: boolean;
  source_format: CsvSourceFormat;
  entities: MigrationEntity[];
  file: {
    name: string;
    size_bytes: number | null;
    rows: number;
    encoding: string;
    /** The normalised header (trimmed, lower-cased). */
    columns: string[];
  };
  columns: {
    /** Header columns the format does not read (also reported by `columns_ignored`). */
    unrecognised: string[];
    /** Optional format columns absent from the header; ignored columns are excluded. */
    missing: string[];
  };
  sensors?: CsvImportEntityCounts;
  locations?: CsvImportEntityCounts;
  deployments?: CsvImportEntityCounts;
  /** Present on a project-scoped import (AKL-814); absent on an organisation-level one. */
  projects?: CsvImportProjectSection[];
  /** Capped at a fixed number of rows per code, in file order. */
  skipped_rows: CsvImportSkippedRow[];
  /** True when any code had more skipped rows than the cap allowed to list. */
  skipped_rows_truncated: boolean;
  warnings: MigrationNotice[];
  /** Non-empty means apply is refused; a blocking notice is always emitted alone. */
  blocking: MigrationNotice[];
};

/**
 * A migration job whose upload was a CSV import. Same row, same endpoints, same lifecycle
 * as `MigrationJobDto`; only the plan/result shape and the echoed options differ.
 */
export type CsvImportJobDto = Omit<
  MigrationJobDto,
  "plan" | "result" | "options"
> & {
  options: CsvImportOptionsType;
  plan: CsvImportPlan | null;
  result: CsvImportPlan | null;
};

/**
 * Is this report a CSV-import plan rather than a Wildlife Insights one?
 *
 * Checked at the render site rather than trusted from the job's `source_format`: the report
 * is read back from a stored job row, and a server ahead of (or behind) this front end could
 * hand back a shape the CSV renderer cannot read. Beyond `kind`, every part the renderer
 * dereferences unconditionally has to be present with the right shape — the `blocking`,
 * `warnings` and `skipped_rows` lists, the `file` block and the `columns` lists — so a
 * version-skewed payload falls back to the "could not read this report" notice instead of
 * throwing during render (PR #761 review).
 * @param plan the job's `plan` or `result`
 * @returns true when the CSV renderer can display it
 */
export const isCsvImportPlan = (plan: unknown): plan is CsvImportPlan => {
  if (typeof plan !== "object" || plan === null) return false;
  const candidate = plan as {
    kind?: unknown;
    blocking?: unknown;
    warnings?: unknown;
    skipped_rows?: unknown;
    file?: unknown;
    columns?: { unrecognised?: unknown; missing?: unknown } | null;
  };
  return (
    candidate.kind === "csv_import" &&
    Array.isArray(candidate.blocking) &&
    Array.isArray(candidate.warnings) &&
    Array.isArray(candidate.skipped_rows) &&
    typeof candidate.file === "object" &&
    candidate.file !== null &&
    Array.isArray((candidate.file as { columns?: unknown }).columns) &&
    typeof candidate.columns === "object" &&
    candidate.columns !== null &&
    Array.isArray(candidate.columns.unrecognised) &&
    Array.isArray(candidate.columns.missing)
  );
};

/**
 * Upload a Wildlife Insights bundle and start its dry run (AKL-747).
 *
 * Returns immediately with a job whose status starts at "uploaded"; the bundle is walked
 * server-side afterwards, so the caller must poll `getMigration` until the status reaches
 * "planned". **Nothing is written to the project by this call** — the resulting `plan`
 * describes what applying it would do.
 *
 * Sent as multipart because the bundle is a file, and a request cannot be both a file
 * upload and a JSON body, so the options ride along as a JSON-encoded form field.
 *
 * Opts out of the interceptor's 5xx redirect (`suppressUnexpectedErrorRedirect`): a server
 * failure here must reach the modal as a rejection, so it can say "try again" rather than
 * the page navigating to `/unexpectedError` and losing the chosen file (AKL-1040). The
 * failure is still recorded for Report a problem, which happens before that branch.
 * @param bundle the WI export bundle (.zip, or a single .csv)
 * @param options where to migrate to, and how
 * @returns the created migration job
 */
export async function createMigration(
  bundle: File,
  options: MigrationOptionsType,
): Promise<MigrationJobDto> {
  try {
    const form = new FormData();
    form.append("bundle", bundle);
    form.append("options", JSON.stringify(options));
    // `Content-Type: undefined` CLEARS it for this request so the browser can set
    // `multipart/form-data` with the boundary it generates. Simply omitting the header is
    // not enough: the shared client sets `application/json` on `defaults.headers.common`,
    // which applies to every request including this one. With a JSON content type on a
    // multipart body the server parses no form fields at all and rejects the request as
    // "bundle: Field required, options: Field required" — which reads like the client sent
    // nothing, rather than like a header problem. Setting the header to
    // `"multipart/form-data"` by hand does NOT work either: it omits the boundary, which
    // only the browser can generate.
    const resp = await axios.post("/migrations", form, {
      headers: { "Content-Type": undefined },
      suppressUnexpectedErrorRedirect: true,
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Fetch the current state of one migration, including its plan once planning finishes.
 *
 * Bypasses the global error interceptor by default because this is polled every couple of
 * seconds while a bundle is walked: a transient failure is retried by the caller, and a
 * toast on every attempt would be noise. Callers surface a persistent failure themselves.
 * @param guid the migration guid
 * @param options set bypassGlobalErrorHandler false for a user-initiated fetch
 * @returns the migration job
 */
export async function getMigration(
  guid: string,
  options: { bypassGlobalErrorHandler?: boolean } = {},
): Promise<MigrationJobDto> {
  const { bypassGlobalErrorHandler = true } = options;
  try {
    const resp = await axios.get(`/migrations/${guid}`, {
      skipGlobalErrorHandler: bypassGlobalErrorHandler,
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Apply a planned migration — the only call in this module that writes.
 *
 * Returns immediately with the job flipped to "applying"; poll `getMigration` until it
 * reaches "applied" or "failed". Refused with a 409 if the plan recorded anything
 * blocking, or if the job has already been applied.
 * @param guid the migration guid
 * @returns the migration job, now applying
 */
export async function applyMigration(guid: string): Promise<MigrationJobDto> {
  try {
    const resp = await axios.post(`/migrations/${guid}/apply`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

// --- CSV imports (AKL-813) -------------------------------------------------
// A CSV import is the same migration job — same endpoints, same lifecycle, same multipart
// header trick — with `source_format` and `entities` in the options. The server chooses the
// plan shape from `source_format`, so the three wrappers below re-type the job once, here,
// and nothing else in the app has to cast. `MigrationJobDto` itself keeps its WI types so
// the project page and `migrationEntry.ts` compile untouched; the render site still guards
// with `isCsvImportPlan` because the shape is a promise, not a guarantee.

/**
 * Upload a CSV and start its dry run (AKL-813). Nothing is written by this call; poll
 * `getCsvImport` until the status reaches "planned".
 * @param file the .csv file
 * @param options the organisation, the CSV format and the entities the file contains
 * @returns the created import job
 */
export async function createCsvImport(
  file: File,
  options: CsvImportOptionsType,
): Promise<CsvImportJobDto> {
  // The server picks the plan shape from `options.source_format`; this is the one cast.
  return (await createMigration(file, options)) as unknown as CsvImportJobDto;
}

/**
 * Fetch the current state of a CSV import, including its plan once planning finishes.
 * Bypasses the global error interceptor like `getMigration`, for the same polling reason.
 * @param guid the import job guid
 * @returns the import job
 */
export async function getCsvImport(guid: string): Promise<CsvImportJobDto> {
  // Same row, CSV-shaped plan: the one cast for this wrapper.
  return (await getMigration(guid)) as unknown as CsvImportJobDto;
}

/**
 * Apply a planned CSV import — the only CSV call that writes. Refused with a 409 while the
 * plan records anything blocking, or if the job has already been applied.
 * @param guid the import job guid
 * @returns the import job, now applying
 */
export async function applyCsvImport(guid: string): Promise<CsvImportJobDto> {
  // Same row, CSV-shaped result: the one cast for this wrapper.
  return (await applyMigration(guid)) as unknown as CsvImportJobDto;
}

/**
 * A project's migration history, newest first.
 * @param projectId the project
 * @param limit how many to return
 * @returns the project's migrations
 */
export async function fetchMigrationsForProject(
  projectId: number,
  limit = 25,
): Promise<MigrationListDto> {
  try {
    const resp = await axios.get(`/migrations/project/${projectId}`, {
      params: { limit },
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/** HTTP status from a rejected request, or undefined when there was no response. */
const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } })?.response?.status;

/**
 * True when a failed status poll is worth retrying — a lost connection (no response at
 * all) or a server-side 5xx. A 4xx is a definitive answer, and retrying it only delays
 * telling the user.
 * @param err the rejected error
 * @returns whether the caller should retry
 */
export const isRetryablePollFailure = (err: unknown): boolean => {
  const status = statusOf(err);
  return status === undefined || status >= 500;
};

/** Statuses where the server is still working and the client should keep polling. */
export const IN_FLIGHT_STATUSES: MigrationStatus[] = [
  "uploaded",
  "planning",
  "applying",
];

/**
 * Whether a migration is still being worked on server-side.
 * @param status the job status
 * @returns whether to keep polling
 */
export const isInFlight = (status: MigrationStatus): boolean =>
  IN_FLIGHT_STATUSES.includes(status);
